import React, { useEffect, useState } from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { TList } from "~/model/Collection/TList";
import { TCatalog_identifier } from "~/model/Instances/TCatalog";
import { CatalogModal } from "~/pages/Editor/EditorDocumentSection/modals/CatalogModal";
import { DeleteCatalogModal } from "~/pages/Editor/EditorDocumentSection/modals/DeleteCatalogModal";
import { CATALOG_EDITOR_OPTIONS } from "~/pages/Editor/EditorDocumentSection/utils/options";
import {
  documentSectionLiterals,
  GetNormalizedCatalogList_EditorDocumentSection,
} from "~/pages/Editor/EditorDocumentSection/utils/utils";
import {
  useGetCatalogLazyQuery,
  useGetCatalogListLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorDocumentSectionColumns } from "./model/tableAdapter";

import s from "./s.less";

enum ModalState {
  edit = "Edit",
  create = "Create",
  remove = "Sure",
}

type Params = {
  id: string;
};
// it's catalog list, but backend dev don't think so =)
export type TSectionsList = TList<TCatalog_identifier>;

const EditorDocumentSection = () => {
  const params = useParams<Params>();
  const { id } = params;
  const { t } = useTranslation();
  const history = useHistory();
  const { openModal, currentId, page, count } = useGetLocationData();
  const { hasPermission } = usePermissions();
  const setURLParamsReplace = useSetLocationData(true);
  const isChildView = !!id;

  const [selectedCatalog, setSelectedCatalog] = useState<TCatalog_identifier>();
  const [parentCatalog, setParentCatalog] = useState<TCatalog_identifier>();
  const [catalogList, setCatalogList] = useState<TSectionsList>(
    documentSectionLiterals.defaultCatalogList,
  );

  /** requests */
  const [getCatalogList, getCatalogListState] = useGetCatalogListLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getCatalog, getCatalogState] = useGetCatalogLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getParentCatalog, getParentCatalogState] = useGetCatalogLazyQuery({
    fetchPolicy: "no-cache",
  });

  const { handleCount, handlePage } = usePaginationParams();

  const openAddModal = () => {
    setURLParamsReplace("openModal", "Create");
  };

  const closeModal = () => {
    setURLParamsReplace("openModal", "", "currentId", "");
  };

  const goBack = () =>
    history.replace(isChildView ? "/editor/doc/" : "/editor/");

  const editModalIsOpen =
    openModal === ModalState.create || openModal === ModalState.edit;
  const deleteModalIsOpen = openModal === ModalState.remove;
  const isEditCatalog = openModal === ModalState.edit;

  const defaultVariablesToRequest = (id) => ({
    variables: {
      parentId: id ? String(id) : undefined,
      page: page || 0,
      count: count || 25,
    },
  });

  const refetchList = (sectionId?: string) => {
    getCatalogList(defaultVariablesToRequest(sectionId));
  };

  useEffect(() => {
    getCatalogList(defaultVariablesToRequest(id));
  }, [id, page, count]);

  useEffect(() => {
    if (id) {
      getParentCatalog({ variables: { id } });
    }
  }, [id]);

  useEffect(() => {
    if (getCatalogListState.data) {
      setCatalogList(
        GetNormalizedCatalogList_EditorDocumentSection(
          getCatalogListState.data,
        ),
      );
    }
  }, [getCatalogListState.data]);

  useEffect(() => {
    if (
      openModal === ModalState.edit &&
      !!currentId &&
      currentId !== "undefined"
    ) {
      getCatalog({ variables: { id: currentId } });
    } else setSelectedCatalog(undefined);
  }, [openModal, currentId]);

  useEffect(() => {
    if (id) {
      getCatalog({ variables: { id: id } });
    } else setParentCatalog(undefined);
  }, [id]);

  useEffect(() => {
    if (getCatalogState.data && openModal && currentId) {
      setSelectedCatalog(
        get(getCatalogState.data, "DocumentGetter.getOneSection", undefined),
      );
    }
  }, [getCatalogState.data, openModal, currentId]);

  useEffect(() => {
    if (getParentCatalogState.data && id) {
      setParentCatalog(
        get(
          getParentCatalogState.data,
          "DocumentGetter.getOneSection",
          undefined,
        ),
      );
    }
  }, [getParentCatalogState.data, id]);

  const handleRowEdit = (record: { id?: string | number }) => {
    setURLParamsReplace("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: { id?: string | number }) => {
    setURLParamsReplace("openModal", "Sure", "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={
        isChildView
          ? `${t("documentLib.editor_title_sub")} ${
              parentCatalog?.name ? parentCatalog?.name : "..."
            }`
          : t("documentLib.editor_title")
      }
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("documents.createSection") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("documentLib.catalogCreate")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={getEditorDocumentSectionColumns(t, CATALOG_EDITOR_OPTIONS)}
        dataSource={catalogList.items}
        loading={getCatalogListState.loading}
        onRowEdit={
          hasPermission("documents.editSection") ? handleRowEdit : undefined
        }
        onRowDelete={
          hasPermission("documents.deleteSection") ? handleRowDelete : undefined
        }
        onRow={(record) => ({
          onClick:
            !isChildView && hasPermission("documents.getFolder")
              ? () => history.push(`/editor/doc/${record.id}`)
              : undefined,
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={catalogList.total || 0}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <CatalogModal
        isOpen={editModalIsOpen}
        isEdit={isEditCatalog}
        isParentData={!selectedCatalog}
        handleCloseModal={closeModal}
        reFetchTable={refetchList}
        selectedCatalog={selectedCatalog}
        parentCatalog={parentCatalog}
        loading={getCatalogState.loading}
      />
      <DeleteCatalogModal
        isOpen={deleteModalIsOpen}
        handleCloseModal={closeModal}
        reFetchTable={refetchList}
      />
    </Wrapper.Content>
  );
};

export default EditorDocumentSection;
